// Lib
import React, { ReactNode, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import classNames from 'classnames';

// Utils
import { getHeaderAvoidanceInsets } from '../../tooltips/tooltipInsetUtils';

// Selectors
import getGridSize from '../../../utils/grid/gridSizeSelector';
import { isPopupIdOpenSelector } from '../../popupPanel/popupOpenSelector';
import { getPlatformFormFactorSelector } from '../../../platform/platformSelector';
import { getCurrentBoard } from '../../../reducers/currentBoardId/currentBoardIdSelector';

// Actions
import { closePopup } from '../../popupPanel/popupActions';

// Hooks
import { useVirtualKeyboardHeightContext } from '../../../utils/keyboard/VirtualKeyboardHeightContext';

// Components
import FloatingPanel from '../../tooltips/FloatingPanel';
import PopupPanelMouseManager from '../../popupPanel/PopupPanelMouseManager';

// Types
import { TooltipPositions } from '../../tooltips/tooltipConstants';

interface EmojiPopupWrapperProps {
    className?: string;
    tooltipPosition?: TooltipPositions;
    popupId: string;
    children: (gridSize: number, closePopup: () => void) => ReactNode;
}

const EmojiPopupWrapperFloatingPanel: React.FC<EmojiPopupWrapperProps> = (props) => {
    const { className, popupId, children, tooltipPosition = TooltipPositions.RIGHT } = props;

    const gridSize = useSelector(getGridSize);
    const currentBoard = useSelector(getCurrentBoard);
    const deviceFormFactor = useSelector(getPlatformFormFactorSelector);

    const dispatch = useDispatch();
    const dispatchClosePopup = useCallback(() => dispatch(closePopup(popupId)), [popupId]);

    const virtualKeyboardHeight = useVirtualKeyboardHeightContext();

    return (
        <FloatingPanel
            selector={`.popup-trigger-${popupId}`}
            className={classNames('EmojiPopupWrapperFloatingPanel', className, deviceFormFactor, 'light')}
            arrowColor="var(--ui-modal-background-primary)"
            arrowStrokeColor="var(--ui-background-primary)"
            arrowWidth={13}
            arrowHeight={8}
            arrowStrokeWidth={0}
            arrowStyle={{ transform: 'translate(0px, 1px)' }}
            distance={15}
            offset={2}
            position={tooltipPosition}
            gridSize={gridSize}
            responsive
            pollPosition
            insets={getHeaderAvoidanceInsets(gridSize, currentBoard, virtualKeyboardHeight)}
        >
            <PopupPanelMouseManager
                {...props}
                gridSize={gridSize}
                visible
                popupId={popupId}
                closePopup={dispatchClosePopup}
            >
                <div className="popup-content">{children(gridSize, dispatchClosePopup)}</div>
            </PopupPanelMouseManager>
        </FloatingPanel>
    );
};

const EmojiPopupWrapper: React.FC<EmojiPopupWrapperProps> = (props) => {
    // @ts-ignore The selector does take two args
    const isPopupOpen = useSelector((state) => isPopupIdOpenSelector(state, props));

    if (!isPopupOpen) return null;

    return <EmojiPopupWrapperFloatingPanel {...props} />;
};

export default EmojiPopupWrapper;
